import {IProduct, IShow, IShowValues, ShowState, ShowType} from '../types/Show'
import type {Readable, Writable} from 'svelte/store'
import {get} from 'svelte/store'
import translate from '../../../lib/translate'
import flatVariantLabel from '../../../lib/flatVariantLabel'
import displayPrice from '../../../lib/displayPrice'
import trackBid from '../../../lib/trackBid'
import {AgentPanel, BidState, ButtonState, IBid, IBidData, INotification, NotificationType} from '../types/Bids'
import label from './BidController.translation'
import {
    agentPanelStore,
    bidStore,
    buttonStore,
    currentProductStore,
    currentVariantStore,
    IBidStore,
    ICurrentVariantStore,
    INotificationsStore,
    ISessionStore,
    notificationsStore,
    sessionStore,
    showStore, valuesStore
} from '../stores'

export class BidController {

    showStore: Writable<IShow>
    valuesStore: Writable<IShowValues>
    currentProductStore: Readable<IProduct|null>
    currentVariantStore: ICurrentVariantStore
    notificationsStore: INotificationsStore
    sessionStore: ISessionStore
    buttonStore: Writable<ButtonState>
    agentPanelStore: Writable<AgentPanel>
    bidStore: IBidStore
    
    bidIdsEventDispatched: Array<string>
    
    currentBid: {
        id?: string,
        showId: number,
        productId: string,
        variantId: string,
        priceCent: number,
        pending: boolean,
    }|null
    
    constructor(
        showStore: Writable<IShow>,
        valuesStore: Writable<IShowValues>,
        currentProductStore: Readable<IProduct|null>,
        currentVariantStore: ICurrentVariantStore,
        notificationsStore: INotificationsStore,
        sessionStore: ISessionStore,
        buttonStore: Writable<ButtonState>,
        agentPanelStore: Writable<AgentPanel>,
        bidStore: IBidStore
    ) {
        this.showStore = showStore
        this.valuesStore = valuesStore
        this.currentProductStore = currentProductStore
        this.currentVariantStore = currentVariantStore
        this.notificationsStore = notificationsStore
        this.sessionStore = sessionStore
        this.buttonStore = buttonStore
        this.agentPanelStore = agentPanelStore
        this.bidStore = bidStore
        this.currentBid = null
        this.bidIdsEventDispatched = []
    }
    
    init() {
        // button state
        
        this.decideButtonState()
        this.sessionStore.subscribe(() => this.decideButtonState())
        this.showStore.subscribe(() => this.decideButtonState())
        this.currentProductStore.subscribe(() => this.decideButtonState())
        this.currentVariantStore.subscribe(() => this.decideButtonState())
        this.agentPanelStore.subscribe(() => this.decideButtonState())
        
        // bind variant to current product and validate + sticky variant behaviour
        
        this.currentProductStore.subscribe((product) => this.observeVariant(product))

        // validate currentBid according to show and currentVariant

        this.showStore.subscribe(() => this.validateCurrentBid())
        this.valuesStore.subscribe(() => this.validateCurrentBid())
        this.currentVariantStore.subscribe(() => this.validateCurrentBid())
        
        // begin fetching bids when logged in

        this.sessionStore.subscribe(session => {
            if(session.authenticated) {
                this.bidStore.start(15000)
            } else {
                this.bidStore.stop()
            }
        })
        
        // observe bidStore for bid-results and current button state
        
        this.bidStore.subscribe((bids) => this.observeBids(bids))
    }
    
    observeVariant(product: IProduct) {
        // deselect variantId when currently no product or product is sold out

        if(!product?.available) {
            this.currentVariantStore.selectVariant(null)
            return
        }

        // product has single variant? -> chose it

        if(product.variants.length === 1) {
            if(product.variants[0].available) {
                this.currentVariantStore.selectVariant(product.variants[0].variantId)
            } else {
                this.currentVariantStore.selectVariant(null)
            }
            return
        }

        let currentVariant = get(this.currentVariantStore)

        // deselect variant when not variant of current product

        if(currentVariant.variant &&
            (
                !currentVariant.variant ||
                product.variants.filter((productVariant) => productVariant.variantId === currentVariant.variant.variantId).length === 0
            )
        ) {
            this.currentVariantStore.selectVariant(null)
        }

        currentVariant = get(this.currentVariantStore)

        // select variant if necessary and possible

        if(!currentVariant.variant) {
            // this will trigger a reevaluation inside the store and select a matching variantId if ringsize and material match
            this.currentVariantStore.selectMaterial(currentVariant.material)
            this.currentVariantStore.selectRingsize(currentVariant.ringsize)
        }
    }
    
    observeBids(bids: Array<IBid>) {
        bids.forEach(bid => {
            
            // allow notification for confirmed / denied current bid

            if( this.currentBid?.id === bid.id && (bid.state === BidState.confirmed || bid.state === BidState.denied)) {
                bid.allowNotify = true
            }
            
            // notification
            
            if(!bid.notified && bid.allowNotify && (bid.state === BidState.confirmed || bid.state === BidState.denied)) {
                bid.notified = true
                let notification = this.createBidNotification(bid)
                this.notificationsStore.add(notification)
            }
            
            // track bid

            if(bid.state === BidState.confirmed && !bid.hasTracked && bid.finalPrice) {
                const hasBidBefore = get(this.sessionStore).hasBidBefore
                const googleAdsConversionTarget = hasBidBefore
                    ? globalThis.config.GoogleAdsConversionTargetBid
                    : globalThis.config.GoogleAdsConversionTargetFirstBid

                this.bidStore.trackBidConversion(bid.showId, bid.id)
                    .then(trackingData => {
                        if(!trackingData) {
                            return    
                        }
                        trackBid(
                            trackingData,
                            hasBidBefore,
                            googleAdsConversionTarget
                        )
                        this.sessionStore.setHasBid()
                    })
            }
            
            this.observeCurrentBidResponse(bid)
        })
    }
    
    createBidNotification(bid: IBid): INotification {
        return <INotification>{
            durationMs: 5000,
            imagePath: bid.product.image.size80,
            title: translate(label.notificationBid(bid.state)),
            message: bid.product.name,
            message2: flatVariantLabel(bid.variant),
            type: bid.state === BidState.confirmed ? NotificationType.confirmation : NotificationType.rejection
        }
    }
    
    observeCurrentBidResponse(bid: IBid) {
        const showId = get(this.showStore).meta?.id
        
        // currentBid answered for (and still same show)?

        if( this.currentBid?.showId === showId &&
            this.currentBid?.id === bid.id
        ) {
            if(bid.state === BidState.confirmed) {
                this.buttonStore.set(ButtonState.bidSuccessful)
            }
            if(bid.state === BidState.denied) {
                this.buttonStore.set(ButtonState.placeBid)
                this.currentBid = null
            }
        }
    }
    
    validateCurrentBid() {
        if(!this.currentBid) {
            return
        }
        // show changed -> reset
        if(this.currentBid.showId !== get(this.showStore).meta?.id) {
            this.currentBid = null
            this.decideButtonState()
            return
        }
        
        // no variant chosen / variant changed -> reset
        if(get(this.currentVariantStore)?.variant?.variantId !== this.currentBid.variantId) {
            this.currentBid = null
            this.decideButtonState()
            return
        }
        
        // update price on currentBid        
        this.currentBid.priceCent = get(this.valuesStore)?.currentPrice.cent || 0
    }
    
    decideButtonState() {
        let session = get(this.sessionStore)
        
        if(!session.authenticated) {
            this.buttonStore.set(ButtonState.loginAndBid)
            return
        }
        if(!session.bidReady) {
            this.buttonStore.set(ButtonState.completeBidData)
            return
        }
        
        let show = get(this.showStore)
        if( show.state !== ShowState.Running
        ) {
            this.buttonStore.set(ButtonState.none)
            return
        }
        
        let product = get(this.currentProductStore)
        if(!product) {
            this.buttonStore.set(ButtonState.none)
            return
        }

        if(get(this.buttonStore) === ButtonState.bidSuccessful) {
            // keep button in state successful
            return
        }
        
        if(this.currentBid) {
            if(this.currentBid.pending) {
                this.buttonStore.set(ButtonState.pleaseWait)
            } else {
                this.buttonStore.set(ButtonState.confirmBid)
            }
            return
        }
        
        if(!product.available) {
            if( show.meta?.type === ShowType.MultiCreation ||
                show.meta?.type === ShowType.MultiProduct
            ) {
                this.buttonStore.set(ButtonState.productSold)
                return
            }
            this.buttonStore.set(ButtonState.none)
            return
        }
        
        let currentVariant = get(this.currentVariantStore)
        if(currentVariant.variant) {
            if(!currentVariant.variant.available) {
                this.buttonStore.set(ButtonState.variantSold)
            } else {
                this.buttonStore.set(ButtonState.placeBid)
            }
        } else {
            this.buttonStore.set(ButtonState.chooseVariant)
        }
    }

    buttonClick(state: ButtonState) {
        
        if(state === ButtonState.loginAndBid) {
            this.agentPanelStore.set(AgentPanel.login)
            return
        }
        
        if(state === ButtonState.completeBidData) {
            this.agentPanelStore.set(AgentPanel.completeData)
            return
        }
        
        if(state === ButtonState.placeBid) {
            this.placeBid()
            return
        }
        
        if(state === ButtonState.confirmBid) {
            this.confirmBid()
            return
        }
    }
    
    placeBid() {
        if(this.currentBid) {
            return
        }

        let showId = get(this.showStore).meta?.id
        let productId = get(this.currentProductStore)?.productId
        let variantId = get(this.currentVariantStore).variant?.variantId
        let priceCent = get(this.valuesStore)?.currentPrice.cent 
        
        if(!showId || !productId || !variantId || !priceCent) {
            this.decideButtonState()
            return
        }

        this.currentBid = {
            showId: showId,
            productId: productId,
            variantId: variantId,
            priceCent: priceCent,
            pending: false, // bit was not yet send
        }
        this.buttonStore.set(ButtonState.confirmBid)

        this.bidStore.prepareBid(
            this.currentBid.showId,
            this.currentBid.productId,
            this.currentBid.variantId,
        )
    }
    
    confirmBid() {
        this.validateCurrentBid()
        
        if(!this.currentBid || this.currentBid.pending) {
            this.decideButtonState()
            return
        }

        this.currentBid.pending = true
        this.buttonStore.set(ButtonState.pleaseWait)

        const bidError = () => {
            this.notificationsStore.add({
                durationMs: 3000,
                type: NotificationType.rejection,
                title: translate(label.serverError)
            })
            this.currentBid = null
            this.decideButtonState()
        }

        const showId = this.currentBid.showId
        const setBid = (bidData: IBidData) => {
            if(!this.currentBid) {
                return
            }
            if(showId !== get(this.showStore).meta?.id) {
                return
            }
            this.currentBid.id = bidData.id
            
            let product = get(this.currentProductStore)
            if(product?.productId === this.currentBid.productId) {
                const bid = <IBid>{
                    id: bidData.id,
                    showId: bidData.showId,
                    state: bidData.state,
                    allowNotify: true,
                    notified: false,
                    bidPrice: {
                        cent: bidData.priceCent,
                        text: displayPrice(bidData.priceCent)
                    },
                    hasTracked: bidData.hasTracked,
                    product: {
                        productId: product.productId,
                        name: product.name,
                        image: {...product.image},
                        type: product.type,
                        availableVariantAspects: {},
                        available: false,
                        brand: null,
                        material: null,
                        materialWeight: null,
                        caratWeight: null,
                        designType: null,
                        dimensions: null,
                        gemStones: [],
                        variants: []
                    },
                    variant: {
                        available: false,
                        variantId: bidData.variantId,
                    }
                }
                if(bidData.material) {
                    bid.variant.material = bidData.material
                }
                if(bidData.ringsize) {
                    bid.variant.ringsize = bidData.ringsize
                }
                if(bidData.finalPriceCent) {
                    bid.finalPrice = {
                        cent: bidData.finalPriceCent,
                        text: displayPrice(bidData.finalPriceCent)
                    }
                }
                this.bidStore.addBid(bid)
            }
        }

        this.bidStore.confirmBid(
            this.currentBid.showId,
            this.currentBid.productId,
            this.currentBid.variantId,
            this.currentBid.priceCent
        )
            .then(bidData => {
                if(bidData) {
                    setBid(bidData)
                } else {
                    bidError()
                }
            })
            .catch(error => {
                console.error(error)
                bidError()
            })

        return
    }
}

let singleton: BidController|null = null

export default (): BidController => {
    if(!singleton) {
        singleton = new BidController(
            showStore,
            valuesStore,
            currentProductStore,
            currentVariantStore,
            notificationsStore,
            sessionStore,
            buttonStore,
            agentPanelStore,
            bidStore
        )
    }
    return singleton
}